import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HeroSection from './components/HeroSection';
import HowItWorksSection from './components/HowItWorksSection';
import FeaturesSection from './components/FeaturesSection';
import TrustSignalsSection from './components/TrustSignalsSection';
import UAESection from './components/UAESection';
import FooterSection from './components/FooterSection';
import AustraliaSection from './components/AustraliaSection';
import BangladeshSection from './components/BangladeshSection';
import { useLanguage } from '../../contexts/LanguageContext';

const LandingPageInner = () => {
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const [activeRegion, setActiveRegion] = useState('australia');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashFadingOut, setSplashFadingOut] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const moreDropdownRef = useRef(null);
  const langDropdownRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreDropdownRef?.current && !moreDropdownRef?.current?.contains(e?.target)) {
        setMoreDropdownOpen(false);
      }
      if (langDropdownRef?.current && !langDropdownRef?.current?.contains(e?.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSplashClick = () => {
    setSplashFadingOut(true);
    setTimeout(() => {
      setSplashVisible(false);
    }, 700);
  };

  const handleLogoClick = () => {
    setActiveRegion('australia');
    setMobileMenuOpen(false);
    setMoreDropdownOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGetStarted = () => {
    navigate('/authentication');
  };

  const scrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    setMoreDropdownOpen(false);
    const element = document.getElementById(sectionId);
    if (element) {
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleRegionSelect = (region) => {
    setActiveRegion(region);
    setTimeout(() => {
      const sectionId = region === 'uae' ? 'uae' : region === 'bangladesh' ? 'bangladesh' : 'australia';
      const element = document.getElementById(sectionId);
      if (element) {
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      <Helmet>
        <title>ListingLens – AI-Powered Property Listings in Australia, UAE & Bangladesh</title>
        <meta name="description" content="List smarter with ListingLens. AI-powered property descriptions, automated market analysis, photo enhancement, and integrated lead capture for Australian, UAE and Bangladesh real estate." />
        <meta name="keywords" content="property listing Australia, real estate UAE, Bangladesh property, AI property description, list property Sydney, list property Dubai, Dhaka real estate, RAJUK approved, property market analysis" />
        <link rel="canonical" href="https://listinglen3865.builtwithrocket.new/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://listinglen3865.builtwithrocket.new/" />
        <meta property="og:title" content="ListingLens – AI-Powered Property Listings in Australia, UAE & Bangladesh" />
        <meta property="og:description" content="List smarter with ListingLens. AI-powered property descriptions, automated market analysis, photo enhancement, and integrated lead capture for Australian, UAE and Bangladesh real estate." />
        <meta property="og:image" content="https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg" />
        <meta property="og:site_name" content="ListingLens" />
        <meta property="og:locale" content="en_AU" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ListingLens – AI-Powered Property Listings in Australia, UAE & Bangladesh" />
        <meta name="twitter:description" content="List smarter with ListingLens. AI-powered property descriptions, automated market analysis, photo enhancement, and integrated lead capture for Australian, UAE and Bangladesh real estate." />
        <meta name="twitter:image" content="https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://listinglen3865.builtwithrocket.new/#organization",
              "name": "ListingLens",
              "url": "https://listinglen3865.builtwithrocket.new/",
              "logo": {
                "@type": "ImageObject",
                "url": "https://listinglen3865.builtwithrocket.new/assets/images/logo_LL-1772022913346.jpeg"
              },
              "description": "AI-powered real estate listing platform for Australian, UAE and Bangladesh property markets.",
              "areaServed": ["Australia", "United Arab Emirates", "Bangladesh"],
              "sameAs": []
            },
            {
              "@type": "WebSite",
              "@id": "https://listinglen3865.builtwithrocket.new/#website",
              "url": "https://listinglen3865.builtwithrocket.new/",
              "name": "ListingLens",
              "publisher": { "@id": "https://listinglen3865.builtwithrocket.new/#organization" },
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://listinglen3865.builtwithrocket.new/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          ]
        })}</script>
      </Helmet>

      {/* ── SPLASH SCREEN ── */}
      {splashVisible && (
        <div
          onClick={handleSplashClick}
          style={{
            position: 'fixed',
            inset: 0,
            background: '#050A14',
            zIndex: 99999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            opacity: splashFadingOut ? 0 : 1,
            transition: 'opacity 0.7s ease-out',
          }}
        >
          {/* Radial glow behind logo */}
          <div style={{
            position: 'absolute',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(0,212,255,0.12) 0%, transparent 70%)',
            pointerEvents: 'none',
          }} />
          {/* Logo container with fixed dimensions to prevent layout shift */}
          <div style={{
            width: '260px',
            height: '260px',
            borderRadius: '50%',
            position: 'relative',
            zIndex: 1,
            flexShrink: 0,
          }}>
            {/* Placeholder shown while image loads */}
            {!logoLoaded && (
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'rgba(0,212,255,0.08)',
                border: '2px solid rgba(0,212,255,0.15)',
              }} />
            )}
            <img
              src="/assets/images/logo_LL-1772022913346.jpeg"
              alt="ListingLens"
              onLoad={() => setLogoLoaded(true)}
              style={{
                width: '260px',
                height: '260px',
                objectFit: 'cover',
                borderRadius: '50%',
                opacity: logoLoaded ? 1 : 0,
                transition: 'opacity 0.4s ease-in',
                animation: logoLoaded ? 'splashLogoIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' : 'none',
                display: 'block',
              }}
            />
          </div>

          <style>{`
            @keyframes splashLogoIn {
              0% { opacity: 0; transform: scale(0.7); }
              100% { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* ── FULL LANDING PAGE ── */}
      {/* AI Network background pattern */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 80% 80%, rgba(123, 47, 255, 0.05) 0%, transparent 50%),
                           radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.03) 0%, transparent 70%)`
        }} />
      </div>

      <nav className="fixed top-0 left-0 right-0" style={{
        background: 'rgba(5, 10, 20, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
        zIndex: 9999,
      }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Left: Logo + Region Buttons */}
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <div className="relative flex-shrink-0">
                <div
                  onClick={handleLogoClick}
                  style={{ display: 'inline-block', cursor: 'pointer' }}
                  aria-label="Go to top of page"
                  role="button"
                >
                  <img
                    src="/assets/images/logo_LL-1772022913346.jpeg"
                    alt="ListingLens logo"
                    className="w-10 h-10 md:w-12 md:h-12 rounded-xl object-cover"
                    style={{
                      boxShadow: '0 0 18px rgba(0, 212, 255, 0.5)',
                      border: '2px solid rgba(0, 212, 255, 0.4)',
                      minWidth: '40px',
                      minHeight: '40px',
                    }}
                  />
                </div>
              </div>
              {/* Region buttons: Australia, UAE, Bangladesh */}
              <div className="hidden md:flex items-center gap-1.5">
                <button
                  onClick={() => handleRegionSelect('australia')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap"
                  style={{
                    background: activeRegion === 'australia' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeRegion === 'australia' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: activeRegion === 'australia' ? '#00D4FF' : '#6B9EC4',
                  }}
                >
                  <span>🇦🇺</span>
                  <span>Australia</span>
                </button>
                <button
                  onClick={() => handleRegionSelect('uae')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap"
                  style={{
                    background: activeRegion === 'uae' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeRegion === 'uae' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: activeRegion === 'uae' ? '#00D4FF' : '#6B9EC4',
                  }}
                >
                  <span>🇦🇪</span>
                  <span>UAE</span>
                </button>
                <button
                  onClick={() => handleRegionSelect('bangladesh')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 whitespace-nowrap"
                  style={{
                    background: activeRegion === 'bangladesh' ? 'rgba(0, 180, 80, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeRegion === 'bangladesh' ? '1px solid rgba(0, 180, 80, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: activeRegion === 'bangladesh' ? '#00B450' : '#6B9EC4',
                  }}
                >
                  <span>🇧🇩</span>
                  <span>Bangladesh</span>
                </button>
              </div>
            </div>

            {/* Center: More dropdown (desktop) */}
            <div className="hidden md:flex items-center gap-1" ref={moreDropdownRef}>
              <div className="relative">
                <button
                  onClick={() => setMoreDropdownOpen(!moreDropdownOpen)}
                  className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                  style={{ color: moreDropdownOpen ? '#00D4FF' : '#6B9EC4' }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#00D4FF'; }}
                  onMouseLeave={e => { if (!moreDropdownOpen) e.currentTarget.style.color = '#6B9EC4'; }}
                >
                  More
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" style={{ transform: moreDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {moreDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl py-2 z-50" style={{ background: 'rgba(5, 10, 20, 0.98)', border: '1px solid rgba(0, 212, 255, 0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    <button onClick={() => { scrollToSection('how-it-works'); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('howItWorks')}</button>
                    <button onClick={() => { scrollToSection('features'); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('features')}</button>
                    <button onClick={() => { navigate('/list-your-property-landing'); setMoreDropdownOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('listProperty')}</button>
                    <button onClick={() => { navigate('/agent-dashboard'); setMoreDropdownOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('agentDashboard')}</button>
                    <div style={{ height: '1px', background: 'rgba(0, 212, 255, 0.1)', margin: '4px 8px' }} />
                    <button onClick={() => { navigate('/professional-marketplace?type=lawyer'); setMoreDropdownOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('lawyers')}</button>
                    <button onClick={() => { navigate('/professional-marketplace?type=photographer'); setMoreDropdownOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('photographers')}</button>
                    <button onClick={() => { navigate('/professional-marketplace?type=stylist'); setMoreDropdownOpen(false); }} className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('stylists')}</button>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Language Selector + Login + Get Started + Hamburger */}
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">

              {/* Language Selector */}
              <div className="relative hidden md:block" ref={langDropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={{
                    background: langDropdownOpen ? 'rgba(0, 212, 255, 0.1)' : 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#6B9EC4',
                  }}
                >
                  <span>{language === 'bn' ? '🇧🇩' : '🌐'}</span>
                  <span>{language === 'bn' ? 'বাংলা' : 'EN'}</span>
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor" style={{ transform: langDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {langDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-36 rounded-xl py-2 z-50" style={{ background: 'rgba(5, 10, 20, 0.98)', border: '1px solid rgba(0, 212, 255, 0.2)', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
                    <button
                      onClick={() => { setLanguage('en'); setLangDropdownOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 flex items-center gap-2"
                      style={{ color: language === 'en' ? '#00D4FF' : '#6B9EC4' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'}
                      onMouseLeave={e => { if (language !== 'en') e.currentTarget.style.color = '#6B9EC4'; }}
                    >
                      <span>🌐</span> English
                      {language === 'en' && <span style={{ color: '#00D4FF', marginLeft: 'auto' }}>✓</span>}
                    </button>
                    <button
                      onClick={() => { setLanguage('bn'); setLangDropdownOpen(false); }}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors duration-200 flex items-center gap-2"
                      style={{ color: language === 'bn' ? '#00B450' : '#6B9EC4' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#00B450'}
                      onMouseLeave={e => { if (language !== 'bn') e.currentTarget.style.color = '#6B9EC4'; }}
                    >
                      <span>🇧🇩</span> বাংলা
                      {language === 'bn' && <span style={{ color: '#00B450', marginLeft: 'auto' }}>✓</span>}
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => navigate('/authentication')}
                className="hidden md:block text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                style={{ color: '#6B9EC4' }}
                onMouseEnter={e => e.target.style.color = '#00D4FF'}
                onMouseLeave={e => e.target.style.color = '#6B9EC4'}
              >
                {t('login')}
              </button>
              <button
                onClick={handleGetStarted}
                className="hidden md:block px-3 py-1.5 md:px-5 md:py-2 rounded-lg text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap"
                style={{
                  background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                  color: '#000000',
                  boxShadow: '0 0 15px rgba(0, 212, 255, 0.4)'
                }}
              >
                {t('getStarted')}
              </button>

              {/* Hamburger - mobile only */}
              <button
                className="md:hidden flex flex-col justify-center items-center gap-1.5 rounded-lg transition-all duration-200"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{ border: '1px solid rgba(0, 212, 255, 0.2)', minWidth: '44px', minHeight: '44px', width: '44px', height: '44px' }}
                aria-label="Toggle menu"
              >
                <span className="block w-5 h-0.5 transition-all duration-300" style={{ background: '#00D4FF', transform: mobileMenuOpen ? 'rotate(45deg) translate(3px, 3px)' : 'none' }} />
                <span className="block w-5 h-0.5 transition-all duration-300" style={{ background: '#00D4FF', opacity: mobileMenuOpen ? 0 : 1 }} />
                <span className="block w-5 h-0.5 transition-all duration-300" style={{ background: '#00D4FF', transform: mobileMenuOpen ? 'rotate(-45deg) translate(3px, -3px)' : 'none' }} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden" style={{ background: 'rgba(5, 10, 20, 0.98)', borderTop: '1px solid rgba(0, 212, 255, 0.15)', borderBottom: '1px solid rgba(0, 212, 255, 0.15)' }}>
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1">
              {/* Region buttons in mobile menu */}
              <div className="flex items-center gap-2 px-3 py-2 flex-wrap">
                <button
                  onClick={() => { handleRegionSelect('australia'); setMobileMenuOpen(false); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={{
                    background: activeRegion === 'australia' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeRegion === 'australia' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: activeRegion === 'australia' ? '#00D4FF' : '#6B9EC4',
                  }}
                >🇦🇺 Australia</button>
                <button
                  onClick={() => { handleRegionSelect('uae'); setMobileMenuOpen(false); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={{
                    background: activeRegion === 'uae' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeRegion === 'uae' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: activeRegion === 'uae' ? '#00D4FF' : '#6B9EC4',
                  }}
                >🇦🇪 UAE</button>
                <button
                  onClick={() => { handleRegionSelect('bangladesh'); setMobileMenuOpen(false); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={{
                    background: activeRegion === 'bangladesh' ? 'rgba(0, 180, 80, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: activeRegion === 'bangladesh' ? '1px solid rgba(0, 180, 80, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: activeRegion === 'bangladesh' ? '#00B450' : '#6B9EC4',
                  }}
                >🇧🇩 Bangladesh</button>
              </div>
              {/* Language toggle in mobile */}
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="text-xs" style={{ color: '#6B9EC4' }}>Language:</span>
                <button
                  onClick={() => setLanguage('en')}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200"
                  style={{
                    background: language === 'en' ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: language === 'en' ? '1px solid rgba(0, 212, 255, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: language === 'en' ? '#00D4FF' : '#6B9EC4',
                  }}
                >🌐 EN</button>
                <button
                  onClick={() => setLanguage('bn')}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold transition-all duration-200"
                  style={{
                    background: language === 'bn' ? 'rgba(0, 180, 80, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: language === 'bn' ? '1px solid rgba(0, 180, 80, 0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: language === 'bn' ? '#00B450' : '#6B9EC4',
                  }}
                >🇧🇩 বাংলা</button>
              </div>
              <div style={{ height: '1px', background: 'rgba(0, 212, 255, 0.1)', margin: '4px 0' }} />
              <button onClick={() => scrollToSection('how-it-works')} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('howItWorks')}</button>
              <button onClick={() => scrollToSection('features')} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('features')}</button>
              <button onClick={() => { navigate('/list-your-property-landing'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('listProperty')}</button>
              <button onClick={() => { navigate('/agent-dashboard'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('agentDashboard')}</button>
              <div style={{ height: '1px', background: 'rgba(0, 212, 255, 0.1)', margin: '4px 0' }} />
              <button onClick={() => { navigate('/professional-marketplace?type=lawyer'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('lawyers')}</button>
              <button onClick={() => { navigate('/professional-marketplace?type=photographer'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('photographers')}</button>
              <button onClick={() => { navigate('/professional-marketplace?type=stylist'); setMobileMenuOpen(false); }} className="text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('stylists')}</button>
              <div style={{ height: '1px', background: 'rgba(0, 212, 255, 0.1)', margin: '4px 0' }} />
              <button onClick={() => { navigate('/authentication'); setMobileMenuOpen(false); }} className="text-left px-3 rounded-lg text-sm font-medium transition-all duration-200" style={{ color: '#6B9EC4', minHeight: '44px', display: 'flex', alignItems: 'center' }} onMouseEnter={e => e.currentTarget.style.color = '#00D4FF'} onMouseLeave={e => e.currentTarget.style.color = '#6B9EC4'}>{t('login')}</button>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-16" style={{ position: 'relative', zIndex: 1 }}>
        <div id="hero">
          <HeroSection onGetStarted={handleGetStarted} activeRegion={activeRegion} onRegionChange={handleRegionSelect} />
        </div>
        <div id="how-it-works">
          <HowItWorksSection />
        </div>
        <div id="features">
          <FeaturesSection />
        </div>
        <div id="trust">
          <TrustSignalsSection />
        </div>
        <div id="australia">
          {activeRegion === 'australia' && <AustraliaSection />}
        </div>
        <div id="uae">
          {activeRegion === 'uae' && <UAESection />}
        </div>
        <div id="bangladesh">
          {activeRegion === 'bangladesh' && <BangladeshSection />}
        </div>
        <FooterSection />
      </main>
    </div>
  );
};

const LandingPage = () => (
  <LandingPageInner />
);

export default LandingPage;